import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatMatchDateTimeLabel } from '../../utils/dateLocale';
import { Card } from '../ui/Card';
import { BetInput } from './BetInput';
import { Button } from '../ui/Button';
import { Lock, Clock } from 'lucide-react';
import { getTeamDisplayName } from '../../utils/teamDisplayName';

export function UpcomingGameCard({
  game,
  homeInput,
  awayInput,
  onHomeChange,
  onAwayChange,
  onSave,
  saving,
  saveDisabled,
  savedFlash,
  language
}) {
  const { isRTL, t } = useLanguage();
  const locked = game.isBettingLocked || game.isLocked;
  const home = typeof game.homeGroupId === 'object' ? game.homeGroupId : {};
  const away = typeof game.awayGroupId === 'object' ? game.awayGroupId : {};

  return (
    <Card hover className="animate-fade-in">
      <div
        dir={isRTL ? 'rtl' : 'ltr'}
        className="grid w-full grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(15rem,18.5rem)] lg:items-start lg:gap-8"
      >
        <div className="min-w-0 w-full space-y-4">
          <div className="flex w-full flex-wrap items-center gap-2">
            {game.round && (
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
                {game.round}
              </span>
            )}
            {locked && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                <Lock className="h-3 w-3" />
                {language === 'en' ? 'Locked' : 'נעול'}
              </span>
            )}
          </div>

          <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-2xl bg-slate-50/90 p-4 ring-1 ring-slate-100 sm:gap-4 sm:p-5">
            <TeamBlock logo={home.logoUrl} name={getTeamDisplayName(home, language)} />
            <div className="flex shrink-0 flex-col items-center px-1">
              <span className="text-center text-[10px] font-bold uppercase tracking-widest text-slate-400 sm:text-xs">
                {t('vs')}
              </span>
            </div>
            <TeamBlock logo={away.logoUrl} name={getTeamDisplayName(away, language)} />
          </div>

          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Clock className="h-4 w-4 shrink-0" aria-hidden />
            <time dateTime={`${game.date}T${game.hour}`} className="tabular-nums">
              {formatMatchDateTimeLabel(game.date, game.hour, language)}
            </time>
          </div>
        </div>

        <div className="min-w-0 w-full border-t border-slate-100 pt-5 lg:border-t-0 lg:border-s lg:border-slate-200 lg:ps-8 lg:pt-0">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {language === 'en' ? 'Your prediction' : 'הניבוי שלך'}
          </p>
          <BetInput
            idPrefix={`g-${game._id}`}
            homeValue={homeInput}
            awayValue={awayInput}
            onHomeChange={onHomeChange}
            onAwayChange={onAwayChange}
            disabled={locked || saveDisabled}
            homeLabel={language === 'en' ? 'Home' : 'בית'}
            awayLabel={language === 'en' ? 'Away' : 'חוץ'}
            isRTL={isRTL}
          />
          <div className="mt-4">
            <Button
              variant="primary"
              className="w-full"
              disabled={locked || saveDisabled}
              loading={saving}
              onClick={onSave}
            >
              {savedFlash
                ? language === 'en'
                  ? 'Saved ✓'
                  : 'נשמר ✓'
                : language === 'en'
                  ? 'Save prediction'
                  : 'שמור ניבוי'}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function TeamBlock({ logo, name }) {
  return (
    <div className="flex min-w-0 flex-col items-center justify-center gap-2 text-center sm:flex-row sm:gap-3">
      <img
        src={logo || '/default-team.png'}
        alt=""
        className="h-11 w-11 shrink-0 rounded-full border-2 border-white object-cover shadow-md ring-2 ring-slate-100 sm:h-12 sm:w-12"
      />
      <span className="line-clamp-2 max-w-full text-sm font-bold leading-snug text-slate-900 sm:text-base">
        {name || '—'}
      </span>
    </div>
  );
}
